/**
 * Phase C: trips by train — Taipei (101, MRT, Palace Museum) and Hualien (Taroko, Qixingtan).
 * Lines marked “English practice” stay in English in every locale on purpose.
 */
export const enTrips = {
  trips: {
    mapNames: { station: 'Railway station', taipei: 'Taipei', mrt: 'MRT station', museum: 'Palace Museum', hualien: 'Hualien', hotel: 'Seaside hotel' },
    zones: {
      station: 'Railway station', taipei: 'Taipei', tower101: 'Taipei 101', taipeiStation: 'Taipei Main Station', taipeiPark: 'City park',
      mrt: 'MRT station', museum: 'Palace Museum', gorge: 'Taroko trail', taroko: 'Taroko Gorge', beach: 'Qixingtan Beach', hualien: 'Hualien', hotel: 'Seaside hotel',
    },
    objects: {
      stTicket: 'Ticket counter', stTrain: 'Train', twElevator: 'Taipei 101 observatory', tpTrain: 'Taipei Main Station', tpNoodles: 'Beef noodle shop',
      tpCakes: 'Pineapple cake shop', ubike: 'YouBike rental', mrtMap: 'MRT route map', mrtRules: 'MRT rules', mrtRide: 'MRT platform',
      muCabbage: 'Jadeite Cabbage', muMeat: 'Meat-shaped Stone', muDing: 'Bronze ding', muRuware: 'Ru ware basin', muScroll: 'Qingming scroll',
      muCurio: 'Curio box', muExit: 'Exit to the MRT', hlHelmet: 'Helmet rental', hlView: 'Swallow Grotto lookout', hlStones: 'Pebble stacking',
      hlSea: 'The Pacific Ocean', hlMochi: 'Mochi shop', hlTrain: 'Hualien Station',
      hlQingshui: 'Qingshui Cliff lookout', hlNineTurns: 'Tunnel of Nine Turns', hlPancake: 'Bomb scallion pancakes', hlWonton: 'Wonton soup stall',
      hlAmis: 'Amis harvest dance', hlWhale: 'Whale-watching boat', htDesk: 'Hotel reception', htBed: 'Your hotel bed',
    },
    npc: { stationClerk: 'Ticket clerk', guide: 'City guide', busker: 'Street musician', docent: 'Museum guide', ranger: 'Park ranger', hiker: 'Hiker', mochiAuntie: 'Mochi auntie', captain: 'Boat captain', receptionist: 'Receptionist' },

    station: {
      counter: 'Good morning! Where would you like to go today?',
      dest: { taipei: 'Taipei', hualien: 'Hualien' },
      destHint: { taipei: 'Taipei 101, the MRT and the Palace Museum', hualien: 'Taroko Gorge and Qixingtan Beach' },
      price: (n: number) => `${n} coins (return)`,
      howAsk: 'How do you ask? (English practice)',
      ask: ['One return ticket to {place}, please.', 'Ticket. {place}. Now.', 'I want to go, give me train.'],
      wrong: 'The clerk smiles: “Could you ask that a little more politely?”',
      bought: (place: string) => `🎫 You bought a return ticket to ${place}. Go to the platform!`,
      poor: 'You don’t have enough coins for that ticket.',
      tooLate: 'The last train of the day has already left. Come back tomorrow morning!',
      haveTicket: (place: string) => `You already have a ticket to ${place}. The platform is just past the gates.`,
      noTicket: 'You need a ticket first — buy one at the counter.',
      board: '🚆 Get on the train',
      boardBack: '🚆 Take the train home',
      notNow: 'Not now',
      trainText: (place: string) => `The train to ${place} is waiting at the platform. Doors are open!`,
      backText: 'Your train home is waiting. Ready to go back?',
    },
    ride: {
      title: '🚆 Train window',
      toTaipei: ['Rice fields shine in the morning sun…', 'Little towns, temples and scooters flash by…', 'Tall buildings… and there’s Taipei 101!'],
      toHualien: ['Green mountains rise on the right…', 'Into a tunnel… and out by the sea!', 'Grey cliffs drop straight into the blue Pacific…'],
      home: ['The train rocks gently…', 'You doze a little, looking out of the window…', 'Almost home!'],
      announce: (place: string) => `“The next station is ${place}.”`,
      skip: '⏩ Skip',
      arrive: (place: string) => `Welcome to ${place}!`,
    },
    taipei: {
      elevator: 'The elevator to the 89th-floor observatory is one of the fastest in the world: 37 seconds!',
      goUp: '🛗 Ride to the top (20 coins)',
      poor: 'You need 20 coins for the observatory.',
      viewTitle: '🏙️ Taipei 101 · 89F',
      viewText: [
        'The whole city is below you: rivers, bridges, mountains and tiny cars.',
        'In the middle of the tower hangs a giant golden ball — the tuned mass damper. It keeps the tower steady during typhoons and earthquakes.',
      ],
      viewAsk: 'English practice: what keeps the tower steady?',
      viewOptions: ['A giant golden ball', 'A long rope', 'Big windows'],
      viewRight: '✅ Right! The damper weighs as much as 660 tonnes.',
      viewWrong: '❌ Not quite — look at the golden ball!',
      noodles: { vendor: 'Welcome! Beef noodle soup — spicy or clear broth?', ok: 'Clear broth, please. One small bowl.', bad: ['Noodles! Hurry!', 'Spicy, but not spicy, but spicy.'], done: 'Tender beef, chewy noodles and a rich soup. You feel super strong!', price: 45 },
      cakes: { vendor: 'Our pineapple cakes are fresh today! Would you like a gift box?', ok: 'Yes, please. A small box for my family, thank you!', bad: ['Cake. Box. Give.', 'I want all of them for free.'], done: 'A pretty box of pineapple cakes. Your family will love them!', price: 40 },
      ubike: 'Shared YouBikes: tap your card, take a bike, return it at any station.',
      rent: '🚲 Rent a bike (5 coins)',
      ride: 'You ride the YouBike! Remember: stop at red lights.',
      returnBike: '🚲 Return the bike',
      returned: 'You return the bike. Beep!',
      onlyHere: 'You return the YouBike before leaving.',
      guide: [
        'Welcome to Taipei! The MRT is the easiest way to get around.',
        'Try the beef noodles — every family has a secret recipe.',
        'From the 101 observatory you can see the mountains around the city.',
        'Excuse me, can you tell me the way to the MRT? Easy: cross the road and turn left!',
      ],
      busker: 'A street musician plays a sweet song on the erhu, a two-stringed fiddle.',
      listen: '🎶 Listen and clap',
      listened: 'You clap along. The musician bows and smiles!',
    },
    mrt: {
      map: 'Colourful lines cross the whole city: red, blue, green, orange, brown…',
      mapQuiz: 'English practice: “Which line goes to the zoo?”',
      mapOptions: ['The brown line', 'The blue line', 'The bus'],
      mapRight: '✅ Yes! The brown line goes to Taipei Zoo.',
      mapWrong: 'Hmm, look at the map again!',
      rules: 'MRT rules — read them in English!',
      rulesQs: [
        { q: 'Can you eat or drink on the MRT?', a: ['No, eating and drinking are not allowed.', 'Yes, a picnic is fine.', 'Only ice cream.'] },
        { q: 'Who are the dark blue priority seats for?', a: ['Elderly people, pregnant women, people with children or injuries.', 'Anyone who runs fast.', 'Only the driver.'] },
        { q: 'Where do you stand on the escalator?', a: ['On the right, so people can walk on the left.', 'In the middle, with my arms out.', 'I sit down on it.'] },
      ],
      rulesRight: '✅ Correct!',
      rulesWrong: '❌ Try again!',
      rulesDone: 'You know the MRT rules like a local!',
      platform: 'The platform screen doors will open when the train arrives. Where are you going?',
      toMuseum: '🏛️ Shilin — Palace Museum',
      toTower: '🏙️ Taipei 101 / World Trade Center',
      toXimen: '🛍️ Ximen — Ximending',
      toLongshan: '🏮 Longshan Temple — Wanhua',
      toCks: '🏯 CKS Memorial Hall — Liberty Square',
      toBeitou: '♨️ Xinbeitou — Beitou Hot Springs',
      riding: 'Ding-dong! “Please mind the gap.”',
    },
    museum: {
      intro: 'English practice: read each label. The guide will ask you one question about it.',
      items: {
        muCabbage: {
          label: 'Jadeite Cabbage. Carved from one piece of jade, half green and half white. Look closely: two little insects hide on the leaves!',
          q: 'What is hiding on the cabbage?',
          a: ['Two little insects', 'A cat', 'A golden coin'],
        },
        muMeat: {
          label: 'Meat-shaped Stone. It looks just like a piece of braised pork, but it is a stone called jasper. The skin even has tiny pores!',
          q: 'What is the “meat” really made of?',
          a: ['Stone', 'Real pork', 'Chocolate'],
        },
        muDing: {
          label: 'Bronze ding. A cooking pot with three legs, almost three thousand years old. It has a long text written inside.',
          q: 'How many legs does a ding have?',
          a: ['Three', 'Four', 'None'],
        },
        muRuware: {
          label: 'Ru ware basin. Sky-blue pottery with fine cracks in the glaze. Fewer than one hundred pieces exist in the whole world.',
          q: 'What colour is Ru ware?',
          a: ['Sky blue', 'Bright red', 'Black'],
        },
        muScroll: {
          label: 'Along the River During the Qingming Festival. A painting more than eleven metres long, full of boats, bridges, shops and thousands of tiny people.',
          q: 'What can you see in the painting?',
          a: ['Boats, bridges and tiny people', 'Only one big tree', 'A football match'],
        },
        muCurio: {
          label: 'Curio box. A small wooden box with secret drawers, made for an emperor to keep his tiny treasures.',
          q: 'Who was the box made for?',
          a: ['An emperor', 'A fisherman', 'A cat'],
        },
      },
      right: '✅ Well read!',
      wrong: '❌ Read the label again!',
      seen: (n: number, total: number) => `Treasures studied: ${n}/${total}`,
      exit: 'Take the MRT back to the city?',
      ride: '🚇 Ride the MRT',
      docent: [
        'Welcome to the Palace Museum. These treasures are hundreds — even thousands — of years old.',
        'Please don’t use a flash when you take photos. Light can damage old paintings.',
        'Our most famous piece? The Jadeite Cabbage. Everyone wants to see the little insects!',
      ],
    },
    shuttle: {
      stop: 'Taiwan Tourist Shuttle — Taroko Route',
      // English practice
      ask: 'Excuse me, does this bus go to Taroko Gorge?',
      where: 'Where would you like to go?',
      city: "🏙️ Hualien Station · Dongdamen · Ji'an",
      taroko: '⛰️ Taroko Gorge',
      qixingtan: '🌊 Qixingtan Beach',
      riding: 'The shuttle winds along the coast road…',
      here: 'You are already here!',
    },
    hualien: {
      helmet: 'Rocks can fall in the gorge. Everybody wears a helmet on the trails!',
      rent: '⛑️ Borrow a helmet (free)',
      wearing: 'You put on a bright yellow helmet. Safety first!',
      already: 'You’re already wearing a helmet.',
      needHelmet: '⛑️ The ranger stops you: “Please wear a helmet on the trail!”',
      view: 'Swallow Grotto: the cliffs are full of little holes where swallows build nests. The river roars far below.',
      look: '🔭 Look carefully',
      looked: 'Swallows dart in and out of the holes. The water is turquoise because of marble dust!',
      viewHelmet: 'You should wear a helmet here. Borrow one at the visitor centre.',
      stones: 'Smooth pebbles everywhere. People stack them into little towers.',
      stack: '🪨 Stack stones',
      sea: 'The Pacific Ocean. The next land that way is… very, very far away!',
      listen: '🌊 Listen to the waves',
      listened: 'Whoosh… crrrsh… the pebbles roll with every wave. So relaxing.',
      welcome: '花蓮歡迎您 — Welcome to Hualien! Mountains on one side, the Pacific on the other.',
      qingshui: 'Qingshui Cliffs: green mountains drop straight into the blue Pacific Ocean, almost a thousand metres! A road is carved into the cliff.',
      lookCliff: '🏔️ Look along the coast',
      lookedCliff: 'The sea is deep blue near the cliffs and turquoise near the shore. A long white line of waves runs below.',
      nineTurns: 'The Tunnel of Nine Turns: the trail winds through the marble like a snake. Look up — the cliffs almost touch!',
      // English practice: food orders below.
      pancake: { vendor: 'Bomb scallion pancake! With an egg inside — spicy sauce or not?', ok: 'A little spicy sauce, please. One with an egg!', bad: ['Pancake. Egg. Quick.', 'Make it boom!'], done: 'Crispy, flaky, and the egg is runny inside. Hot hot hot — but so good!', price: 35 },
      wonton: { vendor: 'Hualien wonton soup — thin skins, pork inside. Small or large bowl?', ok: 'A small bowl, please. Thank you!', bad: ['Soup! Big! Now!', 'Wonton, no soup, no wonton.'], done: 'Silky wontons in a light, clear soup with celery. A Hualien favourite!', price: 40 },
      amis: 'The Amis people, Taiwan’s largest Indigenous group, dance at the summer harvest festival (Ilisin). They hold hands in a big circle and sing together.',
      watchDance: '💃 Watch and clap along',
      danced: 'An auntie smiles and pulls you into the circle! Step, step, kick… You learn the first steps of the dance.',
      whale: 'Whale-watching trips leave from Hualien harbour. Dolphins live here all year, and whales sometimes pass by.',
      boat: (n: number) => `⛴️ Take the boat (${n} coins, 1 hour)`,
      sightings: [
        'Spinner dolphins! Dozens of them jump and spin next to the boat. Everyone cheers!',
        'A huge sperm whale breathes out — pfffff! — and shows its tail before diving.',
        'Flying fish skim over the waves, and a family of bottlenose dolphins swims alongside.',
      ],
      seaFact: 'English practice: “Dolphins are mammals. They breathe air, just like us.”',
      hotel: {
        desk: 'Welcome to the Seaside Hotel! Would you like a room for tonight?',
        price: (n: number) => `🛏️ Book a room (${n} coins)`,
        howAsk: 'How do you ask? (English practice)',
        ask: ['I’d like a room for one night, please.', 'Room. Now. One.', 'Give me the biggest room for free.'],
        wrong: 'The receptionist smiles: “Could you ask that a little more politely?”',
        booked: '🔑 Here is your key: room 302, with a sea view. Sleep there tonight and catch a train home tomorrow!',
        already: 'You already have a room for tonight. Room 302 is through the door on the right.',
        poor: 'You don’t have enough coins for a room.',
        bed: 'A soft bed with clean white sheets, and the sound of waves through the window.',
        sleep: '😴 Go to sleep',
        noRoom: 'This bed belongs to the hotel. Book a room at the reception first.',
        wake: '🌅 Good morning from Hualien! The sun is rising over the Pacific. Your train ticket home is still valid.',
      },
      mochi: { vendor: 'Hualien mochi! Peanut, sesame or red bean?', ok: 'Peanut, please. Can I have two?', bad: ['Mochi now.', 'All flavours, no money.'], done: 'Soft, chewy and sweet. Hualien’s favourite snack!', price: 25 },
      ranger: [
        'Taroko means “magnificent and beautiful” in the language of the Truku people who live here.',
        'Stay on the trail and keep your helmet on, please.',
        'The marble here is millions of years old. The river carved this gorge slowly, day by day.',
      ],
      hiker: [
        'I walked the whole trail! My legs are tired, but the views were worth it.',
        'Did you see the suspension bridge? It wobbles a little — fun!',
        'At Qixingtan you can watch the sunrise over the ocean.',
      ],
    },
  },
};
